import { useState, useEffect } from 'react'
import { formatDateTime, formatDate as formatDateOnly } from '@shared/utils/datetime'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Snackbar,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material'
import { ticketService, WatcherInfo } from '../../../../shared/services/TicketService'
import { Ticket } from '../../../../shared/types/entities'
import { useNotification, useTicketSubscription } from '../../hooks/useSignalR'

function CustomerTicketDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Watch state
  const [isWatching, setIsWatching] = useState(false)
  const [watchers, setWatchers] = useState<WatcherInfo[]>([])
  const [watchLoading, setWatchLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'info' | 'success' | 'error' })

  useEffect(() => {
    loadTicket()
  }, [id])

  const loadTicket = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await ticketService.getTicket(id)
      setTicket(data)

      // Load watch status and watchers if this is a group ticket
      if (data.isGroupTicket) {
        loadWatchStatusForTicket(data.ticketId)
        loadWatchersForTicket(data.ticketId)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }

  const loadWatchStatusForTicket = async (ticketId: string) => {
    try {
      const watching = await ticketService.isWatchingTicket(ticketId)
      setIsWatching(watching)
    } catch (error) {
      console.error('Failed to load watch status:', error)
    }
  }

  const loadWatchersForTicket = async (ticketId: string) => {
    try {
      const watchersList = await ticketService.getWatchers(ticketId)
      setWatchers(watchersList)
    } catch (error) {
      console.error('Failed to load watchers:', error)
    }
  }

  const handleToggleWatch = async () => {
    if (!ticket?.ticketId) return
    setWatchLoading(true)
    try {
      if (isWatching) {
        await ticketService.unwatchTicket(ticket.ticketId)
        setIsWatching(false)
        setSnackbar({ open: true, message: 'You are no longer watching this ticket', severity: 'info' })
      } else {
        await ticketService.watchTicket(ticket.ticketId)
        setIsWatching(true)
        setSnackbar({ open: true, message: 'You are now watching this ticket', severity: 'success' })
      }
      await loadWatchersForTicket(ticket.ticketId)
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update watch status', severity: 'error' })
    } finally {
      setWatchLoading(false)
    }
  }

  const handleTogglePrivacy = async () => {
    if (!ticket?.ticketId) return
    setLoading(true)
    try {
      const newPrivacyState = !ticket.isPrivate
      await ticketService.updateTicket(ticket.ticketId, { isPrivate: newPrivacyState })
      setTicket({ ...ticket, isPrivate: newPrivacyState })
      setSnackbar({
        open: true,
        message: newPrivacyState
          ? 'Ticket is now private - only you and agents can view it'
          : 'Ticket is now shared with your organization',
        severity: 'success'
      })
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update ticket privacy', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'OnHold':
        return 'warning'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string => {
    return formatDateTime(dateString, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !ticket) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer/tickets')}
          sx={{ mb: 3 }}
        >
          Back to Tickets
        </Button>
        <Alert severity="error">
          {error || 'Ticket not found'}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer/tickets')}
        >
          Back to Tickets
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="caption" color="textSecondary" display="block">
                {ticket.ticketNumber}
              </Typography>
              <Typography variant="h4" gutterBottom>
                {ticket.subject}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={ticket.status}
                color={getStatusColor(ticket.status)}
              />
              <Chip
                label={ticket.priority}
                sx={{
                  bgcolor: getPriorityColor(ticket.priority),
                  color: 'white'
                }}
              />
            </Box>
          </Box>

          {ticket?.isGroupTicket && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant={isWatching ? "contained" : "outlined"}
                startIcon={isWatching ? <VisibilityOffIcon /> : <VisibilityIcon />}
                onClick={handleToggleWatch}
                disabled={watchLoading}
                fullWidth
              >
                {isWatching ? 'Watching This Ticket' : 'Watch This Ticket'}
              </Button>
            </Box>
          )}

          {/* Privacy Toggle */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={ticket.isPrivate || false}
                  onChange={handleTogglePrivacy}
                  icon={<LockOpenIcon />}
                  checkedIcon={<LockIcon />}
                  disabled={loading}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    Private Ticket
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {ticket.isPrivate
                      ? 'Only you and agents can view this ticket'
                      : 'Visible to all users in your organization'}
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {ticket.description}
              </Typography>
            </Box>

            <Divider />

            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(ticket.createdAt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {formatDate(ticket.updatedAt)}
                </Typography>
              </Box>

              {ticket.dueDate && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Due Date
                  </Typography>
                  <Typography
                    variant="body2"
                    color={
                      new Date(ticket.dueDate) < new Date() && ticket.status !== 'Resolved'
                        ? 'error'
                        : 'textPrimary'
                    }
                  >
                    {formatDate(ticket.dueDate)}
                  </Typography>
                </Box>
              )}

              {ticket.categoryId && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Category ID
                  </Typography>
                  <Typography variant="body2">
                    {ticket.categoryId}
                  </Typography>
                </Box>
              )}

              {ticket.assignedAgentId && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Assigned Agent ID
                  </Typography>
                  <Typography variant="body2">
                    {ticket.assignedAgentId}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Watchers Card */}
      {ticket?.isGroupTicket && watchers && watchers.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Watchers ({watchers.length})
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={1}>
              {watchers.map((watcher) => (
                <Chip
                  key={watcher.watcherId}
                  label={watcher.userName}
                  size="small"
                  variant="filled"
                  color="secondary"
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Comments Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Comments
          </Typography>
          <CommentsSection ticketId={id!} />
        </CardContent>
      </Card>

      {/* Attachments Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Attachments
          </Typography>
          <AttachmentsSection ticketId={id!} />
        </CardContent>
      </Card>

      {/* Snackbar for watch notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

// Comments Section Component
function CommentsSection({ ticketId }: { ticketId: string }) {
  const [messages, setMessages] = useState<any[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Subscribe to this ticket for real-time updates
  useTicketSubscription(ticketId)

  useEffect(() => {
    loadMessages()
  }, [ticketId])

  // Real-time notification: reload messages when a new message is posted
  useNotification('NewMessage', (data: any) => {
    // Only reload if the message is for this ticket
    if (data.ticketId === ticketId) {
      loadMessages()
    }
  }, [ticketId])

  const loadMessages = async () => {
    try {
      setLoading(true)
      setError(null)
      const { ticketMessageService } = await import('../../../../shared/services/TicketMessageService')
      const data = await ticketMessageService.getTicketMessages(ticketId)
      setMessages(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load comments')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newComment.trim()) return

    try {
      setSubmitting(true)
      setError(null)
      const { ticketMessageService } = await import('../../../../shared/services/TicketMessageService')
      await ticketMessageService.createMessage({
        ticketId,
        content: newComment,
        isInternal: false
      })
      setNewComment('')
      await loadMessages()
    } catch (err: any) {
      setError(err.message || 'Failed to post comment')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error && messages.length === 0) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <Box>
      {messages.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 2 }}>
          No comments yet. Be the first to comment!
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {messages.map((message) => (
            <Box
              key={message.messageId}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {message.authorName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDateTime(message.createdAt)}
                  </Typography>
                </Box>
                {message.isInternal && (
                  <Chip label="Internal" size="small" color="warning" />
                )}
              </Box>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {message.content}
              </Typography>
              {message.attachments && message.attachments.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    Attachments:
                  </Typography>
                  <Stack spacing={1}>
                    {message.attachments.map((att: any) => (
                      <Chip
                        key={att.attachmentId}
                        label={`${att.originalFileName} (${att.fileSizeFormatted})`}
                        size="small"
                        onClick={() => {
                          const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5010'
                          window.open(`${baseUrl}/api/attachments/${att.attachmentId}/download`, '_blank')
                        }}
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}

      <Divider sx={{ my: 3 }} />

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Box
            component="textarea"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            disabled={submitting}
            sx={{
              width: '100%',
              minHeight: '100px',
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              resize: 'vertical',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main'
              }
            }}
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={!newComment.trim() || submitting}
            >
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  )
}

// Attachments Section Component
function AttachmentsSection({ ticketId }: { ticketId: string }) {
  const [attachments, setAttachments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadAttachments()
  }, [ticketId])

  const loadAttachments = async () => {
    try {
      setLoading(true)
      setError(null)
      const { attachmentService } = await import('../../../../shared/services/AttachmentService')
      const data = await attachmentService.getTicketAttachments(ticketId)
      setAttachments(data)
    } catch (err: any) {
      setError(err.message || 'Failed to load attachments')
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    try {
      setUploading(true)
      setError(null)
      const { attachmentService } = await import('../../../../shared/services/AttachmentService')
      await attachmentService.uploadAttachment(file, ticketId)
      await loadAttachments()
      if (e.target) e.target.value = ''
    } catch (err: any) {
      setError(err.message || 'Failed to upload attachment')
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {attachments.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 2 }}>
          No attachments yet.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {attachments.map((att) => (
            <Box
              key={att.attachmentId}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {att.originalFileName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {att.fileSizeFormatted} • Uploaded by {att.uploadedByName} on{' '}
                  {formatDateOnly(att.uploadedAt)}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => {
                  const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5010'
                  window.open(`${baseUrl}/api/attachments/${att.attachmentId}/download`, '_blank')
                }}
              >
                Download
              </Button>
            </Box>
          ))}
        </Stack>
      )}

      <Box>
        <input
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          id="file-upload-input"
        />
        <label htmlFor="file-upload-input">
          <Button
            variant="outlined"
            component="span"
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Attachment'}
          </Button>
        </label>
      </Box>
    </Box>
  )
}

export default CustomerTicketDetail
